import { useMemo, useState } from 'react';
import { Card, Table } from '@quero-delivery/quero-components-web';

import { subYears } from '@querodelivery/qd-date-helper';
import { LaunchBank } from '@/_features/motoboys/motoboy-details/components/launch-bank';
import styles from './styles.module.scss';
import type { TUserType } from '@/Interfaces/Data';
import useModal from '@/hooks/util/useModal';
import { TicketTable } from './ticket-table';
import { PixTable } from './pix-table';
import { AccessPermission } from '@/_components/access-permission';
import { useGetTickets } from '@/_features/places/api/get-tickets';
import { ENUM_TICKET } from '@/_features/places/constants';
import { ExtractsList } from '@/_features/places/components/extracts-list';
import { ExtractsListModal } from '@/_features/places/components/extract-list-modal/extract-list-modal';
import { useGetExtractsPlaces } from '@/_features/places/api/get-extracts-place';
import { useGetPlaceById } from '@/_features/places/api/get-place-by-id';
import { PostPaidSection } from '../../../_features/post-paid/subfeatures/post-paid-section/post-paid-section';

const userType: TUserType = 'PLACE';

export const BankDetails = ({ placeId }: { placeId: string }) => {
  const [showMoreTickets, setShowMoreTickets] = useState(false);
  const [showMoreExtracts, setShowMoreExtracts] = useState(false);
  const [ticketType, setTicketType] = useState<ENUM_TICKET>(ENUM_TICKET.BOLETO);

  const Modal = useModal();

  const { data: tickets, isLoading: isLoadingTickets } = useGetTickets(
    {
      placeId: placeId || '',
    },
    {
      enabled: !!placeId,
    },
  );

  const { data: placeData, isFetching: isLoadingPlace } = useGetPlaceById({
    placeId: placeId || '',
  });

  const [page, setPage] = useState(1);

  const [params, setParams] = useState({
    startDate: subYears(new Date(), 1).toISOString(),
    endDate: new Date().toISOString(),
  });

  const { data: extractsData, isLoading: isLoadingExtracts } =
    useGetExtractsPlaces(
      {
        placeId: placeId || '',
        startDate: params.startDate,
        endDate: params.endDate,
        page,
      },
      {
        enabled: !!placeId && showMoreExtracts,
      },
    );

  const totalCount = extractsData?.total_count;
  const extracts = extractsData?.results;

  function toggleShowMoreExtracts(value: boolean) {
    setShowMoreExtracts(value);
  }

  const table = useMemo(() => {
    const values = tickets
      ?.filter(ticket => ticket.type === ticketType)
      .slice(0, 3);
    if (ticketType === ENUM_TICKET.BOLETO) {
      return <TicketTable tickets={values} />;
    }
    return <PixTable tickets={values} />;
  }, [tickets, ticketType]);

  const tableModal = useMemo(() => {
    const values = tickets?.filter(ticket => ticket.type === ticketType);
    if (ticketType === ENUM_TICKET.BOLETO) {
      return <TicketTable tickets={values} />;
    }
    return <PixTable tickets={values} />;
  }, [tickets, ticketType]);

  return (
    <>
      <AccessPermission permissionKey={['place.credit', 'place.debit']}>
        <Card type="min-shadow" className="mgn-bottom-lg">
          <LaunchBank userType={userType} />
        </Card>
      </AccessPermission>

      <PostPaidSection
        placeData={placeData}
        isLoading={isLoadingPlace}
        placeId={placeId}
      />

      <div className="mgn-bottom-lg">
        <Card type="min-shadow" loading={isLoadingTickets}>
          <div className={styles.titleContainer}>
            <span
              onClick={() => setTicketType(ENUM_TICKET.BOLETO)}
              className={`${
                ticketType === ENUM_TICKET.BOLETO ? styles.active : ''
              }`}
            >
              Boletos
            </span>
            <span
              onClick={() => setTicketType(ENUM_TICKET.PIX)}
              className={`${
                ticketType === ENUM_TICKET.PIX ? styles.active : ''
              }`}
            >
              PIX
            </span>
          </div>
          <Table
            compact
            textAlign="center"
            hasMore={
              tickets && tickets.length > 3 && !showMoreTickets
                ? () => setShowMoreTickets(true)
                : undefined
            }
          >
            {table}
          </Table>
        </Card>
      </div>
      <ExtractsList
        limitShownExtracts={3}
        hasMore={() => {
          toggleShowMoreExtracts(true);
        }}
        id={placeId}
      />

      {showMoreTickets && (
        <Modal
          open={showMoreTickets}
          onClose={() => setShowMoreTickets(false)}
          title={ticketType}
          closeOnDimerClick
        >
          <Card type="shadow" loading={isLoadingTickets}>
            <Table compact textAlign="center">
              {tableModal}
            </Table>
          </Card>
        </Modal>
      )}

      <ExtractsListModal
        open={showMoreExtracts}
        onClose={() => toggleShowMoreExtracts(false)}
        params={params}
        setParams={setParams}
        extracts={extracts || []}
        totalCount={totalCount || 0}
        page={page}
        setPage={setPage}
        isLoading={isLoadingExtracts}
      />
    </>
  );
};
